import type {
  AdCommentSummary,
  CommentStance,
} from "~/ui-rtk/api/commentModerationApi"

const STANCE_CLS: Record<CommentStance, string> = {
  POSITIVE: "bg-emerald-500/20 text-emerald-300",
  NEGATIVE: "bg-rose-500/20 text-rose-300",
  QUESTION: "bg-sky-500/20 text-sky-300",
  NEUTRAL: "bg-system-gray-500/20 text-system-gray-300",
}

const STANCE_LABEL: Record<CommentStance, string> = {
  POSITIVE: "Landed well",
  NEGATIVE: "Pushback",
  QUESTION: "Mostly questions",
  NEUTRAL: "Mixed / neutral",
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString([], { month: "short", day: "numeric" })
}

/**
 * The Gemini-written "what the audience says" for one ad: headline and
 * overall read, the themes people keep raising (with stance), the recurring
 * objections, the questions worth answering, and suggested replies. Shown
 * inside the per-ad reactions row; refreshed on demand or by the daily job.
 */
export default function AdSummaryCard({
  summary,
  onRefresh,
  refreshing,
}: {
  summary: AdCommentSummary | undefined
  onRefresh: () => void
  refreshing: boolean
}) {
  if (!summary) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-basic-blue px-3 py-2 text-sm text-system-gray-400">
        <span>
          No AI summary for this ad yet. The daily run writes one for the
          busiest ads; you can write one now from the comments in the window.
        </span>
        <button
          type="button"
          className="btn btn-outline btn-xs shrink-0"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? "Summarising…" : "Summarise with AI"}
        </button>
      </div>
    )
  }
  const { body } = summary
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-basic-blue bg-basic-dark-blue px-3 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${STANCE_CLS[summary.overall]}`}
            >
              {STANCE_LABEL[summary.overall]}
            </span>
            <span className="text-xs text-system-gray-500">
              {summary.commentCount} comments · {formatDate(summary.windowFrom)}{" "}
              – {formatDate(summary.windowTo)} · AI
            </span>
          </div>
          <p className="mt-1 text-base text-system-gray-100">
            {summary.headline}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-xs shrink-0"
          onClick={onRefresh}
          disabled={refreshing}
          title="Rewrite the summary from the comments currently in the window"
        >
          {refreshing ? "…" : "Refresh"}
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {body.themes.length > 0 && (
          <section>
            <div className="text-xs uppercase tracking-wide text-system-gray-500">
              What people bring up
            </div>
            <ul className="mt-1 flex flex-col gap-1">
              {body.themes.map(t => (
                <li key={t.label} className="flex items-baseline gap-2">
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs ${STANCE_CLS[t.stance]}`}
                  >
                    {t.mentions}
                  </span>
                  <span className="text-system-gray-200">{t.label}</span>
                  {t.example && (
                    <span className="truncate text-system-gray-500">
                      “{t.example}”
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {body.objections.length > 0 && (
          <section>
            <div className="text-xs uppercase tracking-wide text-system-gray-500">
              Pushback
            </div>
            <ul className="mt-1 flex flex-col gap-1">
              {body.objections.map(o => (
                <li key={o.claim} className="text-system-gray-200">
                  <span className="font-mono text-system-gray-500">
                    {o.mentions}×
                  </span>{" "}
                  {o.claim}
                  {o.example && (
                    <span className="text-system-gray-500">
                      {" "}
                      — “{o.example}”
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {body.questions.length > 0 && (
          <section>
            <div className="text-xs uppercase tracking-wide text-system-gray-500">
              Questions to answer
            </div>
            <ul className="mt-1 list-inside list-disc text-system-gray-200">
              {body.questions.map(q => (
                <li key={q}>{q}</li>
              ))}
            </ul>
          </section>
        )}
        {body.suggestedReplies.length > 0 && (
          <section>
            <div className="text-xs uppercase tracking-wide text-system-gray-500">
              Suggested replies
            </div>
            <ul className="mt-1 flex flex-col gap-2">
              {body.suggestedReplies.map(r => (
                <li key={r.to}>
                  <div className="text-system-gray-500">To: {r.to}</div>
                  <div className="text-system-gray-100">{r.reply}</div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  )
}
